<script setup lang="ts">
/**
 * Inactivity warning shown ~1 minute before an idle real user is signed
 * out. Driven by useIdleLogout (wired in App.vue): "Stay signed in" resets
 * the shared idle clock, "Sign out now" ends the session immediately, and
 * ignoring it lets the countdown reach zero and the composable calls
 * authStore.logout().
 *
 * Never shown for dev-bypass sessions — the composable doesn't track them.
 */
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

defineProps<{
    visible: boolean;
    secondsRemaining: number;
}>();

defineEmits<{
    stay: [];
    logoutNow: [];
}>();
</script>

<template>
    <div v-if="visible" class="idle-warning-overlay">
        <div
            class="idle-warning"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="idle-warning-title"
        >
            <h3 id="idle-warning-title" class="idle-warning-title">
                {{ t('idleLogout.title') }}
            </h3>
            <p class="idle-warning-body">
                <i18n-t keypath="idleLogout.body" :plural="secondsRemaining" tag="span">
                    <template #seconds>
                        <strong>{{ secondsRemaining }}</strong>
                    </template>
                </i18n-t>
            </p>
            <div class="idle-warning-actions">
                <button type="button" class="idle-warning-logout" @click="$emit('logoutNow')">
                    {{ t('idleLogout.signOutNow') }}
                </button>
                <button type="button" class="idle-warning-stay" @click="$emit('stay')">
                    {{ t('idleLogout.staySignedIn') }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.idle-warning-overlay {
    position: fixed;
    inset: 0;
    /* Above every other overlay (debug overlays use lower z-indexes) so the
       sign-out warning is never buried. */
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.45);
}

.idle-warning {
    width: 360px;
    max-width: calc(100vw - 2rem);
    background: var(--color-bg-white);
    border: 1px solid var(--color-border);
    border-radius: 8px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
    padding: 1.5rem;
}

.idle-warning-title {
    margin: 0 0 0.5rem;
    font-size: 1.1rem;
    color: var(--color-text-primary);
}

.idle-warning-body {
    margin: 0 0 1.25rem;
    color: var(--color-text-secondary);
    line-height: 1.45;
}

.idle-warning-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.idle-warning button {
    border-radius: 6px;
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    cursor: pointer;
    border: 1px solid transparent;
}

.idle-warning-logout {
    background: transparent;
    border-color: var(--color-border);
    color: var(--color-text-primary);
}

.idle-warning-logout:hover {
    background: var(--color-secondary-hover);
}

.idle-warning-stay {
    background: var(--color-secondary);
    color: var(--color-bg-white);
}

.idle-warning-stay:hover {
    opacity: 0.92;
}
</style>
